import React, { useEffect, useRef, useState } from 'react';
import './index.css'; // Importa os estilos globais
import contact, { getContactMessages, getWhatsAppUrl } from './config/contact';
import { LocaleProvider, useI18n } from './i18n';
import SpotlightSurface from './components/SpotlightSurface';
import Services from './components/Services';
import HeroBackground from './components/HeroBackground';
import Portfolio from './components/Portfolio';

export const NavLink = ({ href, children, className, onClick }) => (
  <a href={href} onClick={onClick} className={className}>
    {children}
  </a>
);

export const Header = () => {
  const { locale, t } = useI18n();
  const messages = getContactMessages(locale);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const headerRef = useRef(null);
  const menuButtonRef = useRef(null);

  const closeMenu = () => setIsMenuOpen(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 16);
    const desktop = window.matchMedia('(min-width: 1001px)');
    let focusFrame = 0;
    const onDesktop = (event) => {
      setIsMenuOpen(false);
      const navigation = headerRef.current?.querySelector('nav');
      let focusTarget;
      if (event.matches && document.activeElement === menuButtonRef.current) {
        focusTarget = navigation?.querySelector('a');
      } else if (!event.matches && navigation?.contains(document.activeElement)) {
        focusTarget = menuButtonRef.current;
      }
      if (focusTarget) {
        cancelAnimationFrame(focusFrame);
        // Wait for the browser to finish hiding the previous responsive control.
        focusFrame = requestAnimationFrame(() => {
          if (document.activeElement === document.body || headerRef.current?.contains(document.activeElement)) {
            focusTarget.focus();
          }
        });
      }
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    desktop.addEventListener('change', onDesktop);
    return () => {
      window.removeEventListener('scroll', onScroll);
      desktop.removeEventListener('change', onDesktop);
      cancelAnimationFrame(focusFrame);
    };
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return;
    const onEscape = (event) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    const onOutsidePointer = (event) => {
      if (!headerRef.current?.contains(event.target)) {
        if (headerRef.current?.contains(document.activeElement)) menuButtonRef.current?.focus();
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onEscape);
    document.addEventListener('pointerdown', onOutsidePointer);
    return () => {
      document.removeEventListener('keydown', onEscape);
      document.removeEventListener('pointerdown', onOutsidePointer);
    };
  }, [isMenuOpen]);

  return (
    <header
      ref={headerRef}
      className={`site-header${isScrolled ? ' is-scrolled' : ''}`}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) closeMenu();
      }}
    >
      <div className="container nav-container">
        <a className="logo" href="#inicio" aria-label={t.header.home} onClick={closeMenu}>
          <img src="/logo-192.png" alt="" className="logo-img" width="40" height="40" />
          <span>Onnix<span className="highlight">Dev</span><span className="logo-dot">.</span></span>
        </a>
        <button
          ref={menuButtonRef}
          className="menu-toggle"
          type="button"
          aria-label={isMenuOpen ? t.header.closeMenu : t.header.openMenu}
          aria-expanded={isMenuOpen}
          aria-controls="primary-navigation"
          onClick={() => setIsMenuOpen((open) => !open)}
        >
          <span className="menu-line" aria-hidden="true" />
          <span className="menu-line" aria-hidden="true" />
        </button>
        <nav className={`site-nav${isMenuOpen ? ' is-open' : ''}`} id="primary-navigation" aria-label={t.header.navigation}>
          <ul className="nav-links">
            <li><NavLink href="#solucoes" onClick={closeMenu}>{t.header.solutions}</NavLink></li>
            <li><NavLink href="#portfolio" onClick={closeMenu}>{t.header.projects}</NavLink></li>
            <li><NavLink href="#sobre" onClick={closeMenu}>{t.header.about}</NavLink></li>
            <li className="language-switch">
              <div role="group" aria-label={t.header.language}>
                <a href="/" lang="pt-BR" hrefLang="pt-BR" aria-label="Português" aria-current={locale === 'pt-BR' ? 'page' : undefined} onClick={closeMenu}>PT</a>
                <span aria-hidden="true">/</span>
                <a href="/en/" lang="en" hrefLang="en" aria-label="English" aria-current={locale === 'en' ? 'page' : undefined} onClick={closeMenu}>EN</a>
              </div>
            </li>
            <li><a href={getWhatsAppUrl(messages.general)} className="btn-nav" target="_blank" rel="noopener noreferrer" onClick={() => {
              closeMenu();
              if (isMenuOpen) menuButtonRef.current?.focus();
            }}>{t.header.contact} <span aria-hidden="true">↗</span></a></li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export const Hero = () => {
  const { locale, t } = useI18n();
  const messages = getContactMessages(locale);
  return (
  <section className="hero" id="inicio" tabIndex={-1} aria-labelledby="hero-title">
    <HeroBackground />
    <div className="container hero-content">
      <div className="hero-intro">
        <p className="hero-eyebrow"><span aria-hidden="true" />{t.hero.eyebrow}</p>
        <h1 id="hero-title">
          <span>{t.hero.titleStart}</span>{' '}
          <span>{t.hero.titleBridge} <span className="hero-title-accent">{t.hero.titleAccent}</span></span>
        </h1>
        <p className="hero-description">{t.hero.description}</p>
        <div className="hero-buttons">
          <a href={getWhatsAppUrl(messages.general)} target="_blank" rel="noopener noreferrer" className="btn-primary">{t.hero.primaryCta} <span aria-hidden="true">↗</span></a>
          <NavLink href="#portfolio" className="btn-secondary">{t.hero.secondaryCta} <span aria-hidden="true">↓</span></NavLink>
        </div>
      </div>
      <ul className="hero-capabilities" aria-label={t.hero.capabilitiesLabel}>
        {t.hero.capabilities.map((item) => <li key={item.label}>
          <span className="capability-label">{item.label}</span>
          <strong>{item.title}</strong>
          <p>{item.description}</p>
        </li>)}
      </ul>
    </div>
  </section>
  );
};

export const Problems = () => {
  const { t } = useI18n();
  return (
  <section id="problemas" className="problems" tabIndex={-1} aria-labelledby="value-title">
    <div className="container value-layout">
      <div className="value-copy">
        <p className="eyebrow">{t.value.eyebrow}</p>
        <h2 id="value-title">{t.value.title}</h2>
        <p>{t.value.description}</p>
        <NavLink className="section-link" href="#solucoes">{t.value.link} <span aria-hidden="true">↓</span></NavLink>
      </div>
      <ol className="value-outcomes" aria-label={t.value.listLabel}>
        {t.value.outcomes.map((item, index) => <li key={item.title}>
          <span className="value-number" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
          <div>
            <h3>{item.title}</h3>
            <p>{item.problem}</p>
            <p className="value-result">{item.result}</p>
          </div>
        </li>)}
      </ol>
    </div>
  </section>
  );
};

const Comparison = () => {
  const { locale, t } = useI18n();
  const messages = getContactMessages(locale);
  return (
  <section className="comparison" id="comparacao" tabIndex={-1}>
    <div className="container">
      <div className="section-header">
        <p className="eyebrow">{t.comparison.eyebrow}</p>
        <h2>{t.comparison.title}</h2>
      </div>
      <div className="comparison-table motion-grid">
        {t.comparison.cards.map((card, index) => <div className={`comparison-card${index === 2 ? ' highlighted' : ''}`} key={card.title}>
          <h3>{card.title}</h3>
          <ul>{card.points.map((point) => <li key={point}>{point}</li>)}</ul>
        </div>)}
      </div>
      <SpotlightSurface className="cta-inline" id="cta-comparacao">
        <h3>{t.comparison.contactTitle}</h3>
        <a href={getWhatsAppUrl(messages.consultation)} target="_blank" rel="noopener noreferrer" className="btn-primary">{t.comparison.contactCta}</a>
      </SpotlightSurface>
    </div>
  </section>
  );
};

const FitSection = () => {
  const { t } = useI18n();
  return (
  <section className="fit-section" id="fit" tabIndex={-1}>
    <div className="container">
      <div className="section-header">
        <p className="eyebrow">{t.fit.eyebrow}</p>
        <h2>{t.fit.title}</h2>
      </div>
      <div className="fit-grid motion-grid">
        <div className="fit-card">
          <h3>{t.fit.notFor.title}</h3>
          <ul>{t.fit.notFor.points.map((point) => <li key={point}>{point}</li>)}</ul>
        </div>
        <div className="fit-card good">
          <h3>{t.fit.for.title}</h3>
          <ul>{t.fit.for.points.map((point) => <li key={point}>{point}</li>)}</ul>
        </div>
      </div>
    </div>
  </section>
  );
};

const ProcessSection = () => {
  const { t } = useI18n();
  return (
  <section className="process" id="processo" tabIndex={-1}>
    <div className="container">
      <div className="section-header">
        <p className="eyebrow">{t.process.eyebrow}</p>
        <h2>{t.process.title}</h2>
      </div>
      <div className="timeline motion-grid">
        {t.process.steps.map((step, index) => <div className="step" key={step.title}>
          <span>{String(index + 1).padStart(2, '0')}</span>
          <h3>{step.title}</h3>
          <p>{step.description}</p>
        </div>)}
      </div>
    </div>
  </section>
  );
};

const About = () => {
  const { t } = useI18n();
  return (
  <section className="about" id="sobre" tabIndex={-1}>
    <div className="container about-grid motion-grid">
      <div>
        <p className="eyebrow">{t.about.eyebrow}</p>
        <h2>{t.about.title}</h2>
        <p>{t.about.description}</p>
      </div>
      <div className="about-card">
        <h3>{t.about.why}</h3>
        <ul>{t.about.points.map((point) => <li key={point}>{point}</li>)}</ul>
      </div>
    </div>
  </section>
  );
};

export const ContactSection = () => {
  const { locale, t } = useI18n();
  const messages = getContactMessages(locale);
  return (
  <section id="contato" className="cta-section" tabIndex={-1} aria-labelledby="contact-title">
    <div className="container">
      <div className="contact-panel">
        <div className="contact-main">
          <p className="eyebrow">{t.contact.eyebrow}</p>
          <h2 id="contact-title">{t.contact.title}</h2>
          <p className="contact-description">{t.contact.description}</p>
          <div className="contact-actions">
            <a href={getWhatsAppUrl(messages.general)} target="_blank" rel="noopener noreferrer" className="btn-primary">{t.contact.primaryCta} <span aria-hidden="true">↗</span></a>
            <a href={`mailto:${contact.email}`} className="btn-secondary">{t.contact.emailCta} <span aria-hidden="true">↗</span></a>
          </div>
        </div>
        <div className="contact-next-step">
          <p className="contact-step-label">{t.contact.nextStep}</p>
          {t.contact.steps.map((step, index) => <p key={step}><span>{String(index + 1).padStart(2, '0')}</span> {step}</p>)}
        </div>
      </div>
    </div>
  </section>
  );
};

export const Footer = () => {
  const { locale, t } = useI18n();
  const messages = getContactMessages(locale);
  return (
  <footer className="site-footer">
    <div className="container">
      <div className="footer-grid">
        <div className="footer-brand">
          <a className="footer-logo" href="#inicio" aria-label={t.header.home}>
            <img src="/logo-192.png" alt="" className="footer-logo-img" width="32" height="32" />
            <span>Onnix<span className="highlight">Dev</span>.</span>
          </a>
          <p>{t.footer.description}</p>
        </div>
        <nav aria-label={t.footer.navigationLabel} className="footer-column">
          <h2>{t.footer.navigation}</h2>
          <ul>
            <li><a href="#inicio">{t.footer.home}</a></li>
            <li><a href="#solucoes">{t.footer.services}</a></li>
            <li><a href="#portfolio">{t.footer.projects}</a></li>
            <li><a href="#contato">{t.footer.contact}</a></li>
          </ul>
        </nav>
        <div className="footer-column">
          <h2>{t.footer.solutions}</h2>
          <ul>
            {t.footer.serviceNames.map((name) => <li key={name}>{name}</li>)}
          </ul>
          <a className="footer-section-link" href="#solucoes">{t.footer.explore} <span aria-hidden="true">↗</span></a>
        </div>
        <div className="footer-column">
          <h2>{t.footer.contactTitle}</h2>
          <ul>
            <li><a href={getWhatsAppUrl(messages.general)} target="_blank" rel="noopener noreferrer">WhatsApp</a></li>
            <li><a href={`mailto:${contact.email}`}>{contact.email}</a></li>
            <li><a href={contact.instagram} target="_blank" rel="noopener noreferrer">Instagram <span aria-hidden="true">↗</span></a></li>
            <li><a href={contact.linkedin} target="_blank" rel="noopener noreferrer">LinkedIn <span aria-hidden="true">↗</span></a></li>
          </ul>
        </div>
      </div>
      <div className="footer-bottom">
        <p>&copy; {new Date().getFullYear()} Onnix Dev.</p>
        <p>{t.footer.independent}</p>
      </div>
    </div>
  </footer>
  );
};

const App = () => {
  useEffect(() => {
    const getTarget = () => {
      let id;
      try {
        id = decodeURIComponent(window.location.hash.slice(1));
      } catch {
        return null;
      }
      const target = document.getElementById(id);
      return target?.matches('main section[id]') ? target : null;
    };
    const focusTarget = () => {
      const target = getTarget() || (!window.location.hash ? document.getElementById('inicio') : null);
      target?.focus({ preventScroll: true });
    };
    // React mounts sections after the browser first resolves the incoming fragment.
    // Only the initial deep link needs a scroll correction; clicks remain native.
    const frame = requestAnimationFrame(() => {
      const target = getTarget();
      if (target) {
        target.scrollIntoView({ behavior: 'instant' });
        target.focus({ preventScroll: true });
      }
    });
    window.addEventListener('hashchange', focusTarget);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('hashchange', focusTarget);
    };
  }, []);

  return (
    <LocaleProvider>
      <Header />
      <main>
        <Hero />
        <Problems />
        <Services />
        <Portfolio />
        <Comparison />
        <FitSection />
        <ProcessSection />
        <About />
        <ContactSection />
      </main>
      <Footer />
    </LocaleProvider>
  );
};

export default App;
